import { Button, Group, Stack, Text } from "@mantine/core";
import { SimpleBreadcrumbs } from "../../components/SimpleBreadcrumbs";
import { ActionCard } from "../../components/ActionCard";
import type { UserDetails } from "@project/api";
import type { FC } from "react";
import { FullPageLoader } from "../../components/FullPageLoader";
import { useDisclosure } from "@mantine/hooks";
import { Avatar } from "../../components/Avatar";
import { ChangeEmailModal } from "./components/ChangeEmailModal";
import { ProviderType } from "@project/common";
import { DeleteUserModal } from "./components/DeleteUserModal";
import { ChangeAvatarDropzone } from "./components/ChangeAvatarDropzone";
import { MarketingConsentSwitch } from "./components/MarketingConsentSwitch";
import { LanguagePicker } from "../../layouts/components/LanguagePicker";
import { FormattedMessage, useIntl } from "react-intl";
import { useUser } from "../../hooks/user";

const UserAccountsDisplay: FC<{ user: UserDetails }> = ({ user: { accounts } }) => {
  return (
    <Stack gap="2px">
      <Text>
        <FormattedMessage
          defaultMessage="User belong to following accounts:"
          id="text.userBelongToAccounts"
        />
      </Text>
      {accounts.map(({ id, name }) => (
        <Text key={id} fs="italic">
          {name}
        </Text>
      ),
      )}
    </Stack>
  );
};

const UserAvatarDisplay: FC<{ user: UserDetails }> = ({ user }) => {
  return (
    <Group justify="center">
      <Avatar
        user={user}
        variant="outline"
        size="8rem"
      />
    </Group>
  );
};

export const Preferences = () => {
  const intl = useIntl();
  const user = useUser();

  const [
    deleteOpened,
    {
      open: openDeleteUser,
      close: closeDeleteUser,
    },
  ] = useDisclosure(false);

  const [
    changeEmailOpened,
    {
      open: openChangeEmail,
      close: closeChangeEmail,
    },
  ] = useDisclosure(false);

  if (!user) {
    return <FullPageLoader />;
  }

  return (
    <Stack gap={16} h="100%">
      <SimpleBreadcrumbs
        parent={intl.formatMessage({ defaultMessage: "Manage profile", id: "navigation.manageProfile" })}
        current={intl.formatMessage({ defaultMessage: "Preferences", id: "navigation.preferences" })}
        description={intl.formatMessage({ defaultMessage: "Change email address, language and other preferences.", id: "text.preferencesDescription" })}
      />
      <Stack gap={16}>
        {user.currentProvider.type === ProviderType.Email && (
          <ActionCard
            title={intl.formatMessage({ defaultMessage: "Email address", id: "title.emailAddress" })}
            subTitle={user.email}
          >
            <Button onClick={openChangeEmail} variant="primary">
              {intl.formatMessage({ defaultMessage: "Change email address", id: "button.changeEmailAddress" })}
            </Button>
          </ActionCard>
        )}

        <ActionCard
          title={intl.formatMessage({ defaultMessage: "User avatar", id: "title.userAvatar" })}
          subTitle={<UserAvatarDisplay user={user} />}
        >
          <ChangeAvatarDropzone user={user} />
        </ActionCard>

        <ActionCard
          title={intl.formatMessage({ defaultMessage: "Communication preferences", id: "title.communicationPreferences" })}
          subTitle={intl.formatMessage({ defaultMessage: "We may send you product updates, offers and other marketing communications.", id: "text.communicationPreferencesDescription" })}
        >
          <MarketingConsentSwitch user={user} />
        </ActionCard>

        <ActionCard
          title={intl.formatMessage({ defaultMessage: "Language Preference", id: "title.languagePreference" })}
          subTitle={intl.formatMessage({ defaultMessage: "My preference for the language shown in the dashboard is:", id: "text.languagePreferenceDescription" })}
        >
          <LanguagePicker />
        </ActionCard>

        <ActionCard
          title={intl.formatMessage({ defaultMessage: "User", id: "title.user" })}
          subTitle={<UserAccountsDisplay user={user} />}
        >
          <Button variant="danger" onClick={openDeleteUser}>
            {intl.formatMessage({ defaultMessage: "Delete this user", id: "button.deleteThisUser" })}
          </Button>
        </ActionCard>
      </Stack>

      <DeleteUserModal
        onClose={closeDeleteUser}
        open={deleteOpened}
      />

      <ChangeEmailModal
        user={user}
        open={changeEmailOpened}
        onClose={closeChangeEmail}
      />
    </Stack>
  );
};
